import React, { useEffect, useContext } from "react"
import PropTypes from "prop-types"
import styled from "styled-components"
import { motion, useAnimation } from "framer-motion"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {faCommentDots, faInfoCircle, faExternalLinkAlt} from '@fortawesome/free-solid-svg-icons'
import Context from "context/"
import ContentWrapper from "styles/contentWrapper"
import { lightTheme, darkTheme } from "styles/theme"
import spins from "content/images/spins.svg"
import Video from "components/common/video"
import StyledBtn from "styles/button"

const StyledSection = styled.section`
  width: 100%;
  height: auto;
  background: ${({ theme }) => theme.colors.zodiacDark};
  background-image: url(${spins});
`

const StyledContentWrapper = styled(ContentWrapper)`
  && {
    width: 100%;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    text-align: center;
  /*   padding-top: 5.68rem;
    padding-bottom: 7.68rem; */
    padding-top: 3rem;
    padding-bottom: 7.68rem;
    @media (min-width: ${({ theme }) => theme.breakpoints.lg}) {
      padding-bottom: ${props => props.videoUrl ? "3rem" : "7.68rem"};

    }
  }
`

const StyledWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  grid-column-gap: 2rem;
  .content {
    text-align: left;
    margin-bottom: 2rem;
    .logo {
      width: 3rem;
      height: 3rem;
      min-height: 3rem;
      border-radius: 0.5rem;
      display: flex;
      justify-content: center;
      align-items: center;
      margin-bottom: 1rem;
      background: ${props =>props.color};
      img {
        height: 1.7rem;
        width: auto;
      }
    }
    .title {
      color: white;
      margin-bottom: 1rem;
    }
    .subtitle {
      color: ${({ theme }) => theme.colors.primary};
      font-weight: 400;
      font-size: 1.12rem;
      line-height: 1.93rem;
      max-width: 32rem;
    }
  }
  .buttons {
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.lg}) {
    display: grid;
    grid-template-columns: 52.31rem auto;
    grid-column-gap: 2rem;
    .content {
      margin-bottom: 0;
    }
    .buttons {
      display: flex;
      flex-direction: column;
      align-items: flex-end;
    }
  }
`
const StyledButton = styled(StyledBtn).attrs({
  as: "a"
})`
  background: ${({ theme }) => theme.colors.zodiacDark};
  border: 1px solid ${({ theme }) => theme.colors.zodiac};
  border-radius: 0.5rem;
  padding: 0 1rem;
  display: flex;
  justify-content: space-between; 
  align-items: center;
  width: 100%;
  height: 3.12rem;
  font-size: 0.94rem;
  line-height: 1.2rem;
  color: ${({ theme }) => theme.colors.tertiary};
  &:nth-of-type(2) {
    margin: 0.75rem 0;
  }
  @media (min-width: ${({ theme }) => theme.breakpoints.lg}) {
    // width: 17.31rem;
  }
`


const Hero = ({ content, logo, color, resultPage, docs, videoUrl, videoText }) => {
  const { frontmatter } = content[0].node
  const { isIntroDone, darkMode } = useContext(Context).state

  // Controls to orchestrate animations of greetings, emoji, social profiles, underlining
  const gControls = useAnimation()
  const eControls = useAnimation()
  const sControls = useAnimation()
  const uControls = useAnimation()

  // Start Animations after the splashScreen sequence is done
  useEffect(() => {
    const pageLoadSequence = async () => {
      if (isIntroDone) {
        eControls.start({
          rotate: [0, -10, 12, -10, 9, 0, 0, 0, 0, 0, 0],
          transition: { duration: 2.5, loop: 3, repeatDelay: 1 },
        })
        await gControls.start({
          opacity: 1,
          y: 0,
          transition: { delay: 0.4 },
        })
        await sControls.start({
          opacity: 1,
          x: 0,
        })
        // Animate underlining to hover state
        await uControls.start({
          boxShadow: `inset 0 -2rem 0 ${
            darkMode ? darkTheme.colors.secondary : lightTheme.colors.secondary
          }`,
          transition: { delay: 0.4, ease: "circOut" },
        })
      }
    }
    pageLoadSequence()
  }, [isIntroDone, darkMode, eControls, gControls, sControls, uControls])

  return (
    <StyledSection id="hero">
      <StyledContentWrapper videoUrl={videoUrl}>
        <StyledWrapper color={color}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={gControls}
            className="content"
          >
            {logo && (
              <div className="logo">
                <img src={logo} />
              </div>
            )}
            <h1 className="title mb-05">
            {frontmatter.title}{' '}{resultPage && 'result'} 
            </h1>
            <h2 className="subtitle">
              {frontmatter.description}
            </h2>
          </motion.div>
          <motion.div
            className="buttons"
          >
            <StyledButton href={docs} target="_blank" rel="noreferrer">
              <span>View documentation</span>
              <FontAwesomeIcon width="20" height= "20" icon={faInfoCircle} />
            </StyledButton>
         {/*    <StyledButton href="#">
              <span>Send to a coworker</span>
              <FontAwesomeIcon  icon={faExternalLinkAlt} />
            </StyledButton> */}
            <StyledButton href="https://hrflow.ai/book-us" target="_blank" rel="noreferrer">
              <span>Talk to an expert</span>
              <FontAwesomeIcon width="20" height= "20"  icon={faCommentDots} />
            </StyledButton>
            {videoUrl && (
              <Video url={videoUrl} text={videoText} />
            )}
          </motion.div>
        </StyledWrapper>
      </ StyledContentWrapper>
    </StyledSection>
  )
}
Hero.propTypes = {
  content: PropTypes.arrayOf(
    PropTypes.shape({
      node: PropTypes.shape({
        body: PropTypes.string.isRequired,
        frontmatter: PropTypes.object.isRequired,
      }).isRequired,
    }).isRequired
  ).isRequired,
}

export default Hero
